import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import NavigationBar from '../components/NavigationBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Check, AlertTriangle, Clock, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const Subscriptions = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [payerFullName, setPayerFullName] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentSecondsLeft, setPaymentSecondsLeft] = useState(15 * 60);
  const paymentDeadlineRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!showPaymentModal || !paymentDeadlineRef.current) {
      return undefined;
    }

    const updateTimer = () => {
      const secondsLeft = Math.max(0, Math.ceil((paymentDeadlineRef.current - Date.now()) / 1000));
      setPaymentSecondsLeft(secondsLeft);

      if (secondsLeft === 0) {
        paymentDeadlineRef.current = null;
        setShowPaymentModal(false);
        setSelectedPlan(null);
        setPayerFullName('');
        toast.info('Время для оплаты истекло. При необходимости откройте тариф снова.');
      }
    };

    updateTimer();
    const timerId = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(timerId);
  }, [showPaymentModal]);

  const loadData = async () => {
    try {
      const [plansResponse, statusResponse, userResponse, paymentDetailsResponse] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/my-status'),
        api.get('/auth/me'),
        api.get('/subscriptions/payment-details')
      ]);

      setPlans(plansResponse.data);
      setStatus(statusResponse.data);
      setPaymentDetails(paymentDetailsResponse.data);

      // Check if user has active subscription
      const userData = userResponse.data;
      if (userData.active_subscription && userData.subscription_expires_at) {
        const expiresAt = new Date(userData.subscription_expires_at);
        if (expiresAt > new Date()) {
          setCurrentPlan(userData.active_subscription);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseClick = (planName, enabled) => {
    if (!enabled) {
      toast.error('Тариф временно не доступен');
      return;
    }

    // If user already has a subscription, show confirmation
    if (currentPlan && currentPlan !== planName) {
      setSelectedPlan(planName);
      setShowConfirmModal(true);
    } else {
      openPayment(planName);
    }
  };

  const openPayment = (planName) => {
    const paymentWindowMinutes = paymentDetails?.payment_window_minutes || 15;
    setSelectedPlan(planName);
    setPayerFullName(user?.name || '');
    setPaymentSecondsLeft(paymentWindowMinutes * 60);
    paymentDeadlineRef.current = Date.now() + paymentWindowMinutes * 60 * 1000;
    setShowConfirmModal(false);
    setShowPaymentModal(true);
  };

  const closePayment = () => {
    paymentDeadlineRef.current = null;
    setShowPaymentModal(false);
    setSelectedPlan(null);
    setPayerFullName('');
  };

  const copyPaymentPhone = async () => {
    const phoneToCopy = paymentDetails?.recipient_phone_copy || paymentDetails?.recipient_phone;
    if (!phoneToCopy) {
      toast.error('Номер для оплаты пока недоступен');
      return;
    }

    const legacyCopy = () => {
      const input = document.createElement('textarea');
      input.value = phoneToCopy;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      input.style.pointerEvents = 'none';
      document.body.appendChild(input);
      input.focus();
      input.select();
      input.setSelectionRange(0, input.value.length);
      const copied = document.execCommand('copy');
      document.body.removeChild(input);
      if (!copied) throw new Error('copy_not_supported');
    };

    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(phoneToCopy);
      } else {
        legacyCopy();
      }
      toast.success('Номер +7 скопирован');
    } catch (error) {
      try {
        legacyCopy();
        toast.success('Номер +7 скопирован');
      } catch (fallbackError) {
        toast.error('Не удалось скопировать номер. Нажмите и удерживайте номер, чтобы скопировать вручную.');
      }
    }
  };

  const handlePaymentRequest = async () => {
    const normalizedName = payerFullName.trim();
    if (!selectedPlan || normalizedName.length < 2) {
      toast.error('Укажите имя отправителя перевода');
      return;
    }

    setSubmittingPayment(true);
    try {
      const response = await api.post('/subscriptions/payment-request', {
        plan_name: selectedPlan,
        payer_full_name: normalizedName
      });
      toast.success(response.data.message);
      closePayment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось отправить заявку на оплату');
    } finally {
      setSubmittingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-[#7A7A7A]">Загрузка...</p>
        </div>
      </div>
    );
  }

  // Plan colors without gradients
  const planColors = ['#9CA3AF', '#F59E0B', '#8B5CF6']; // Silver, Gold, VIP
  const selectedPlanDetails = plans.find((plan) => plan.name === selectedPlan);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#F8F9FA] via-white to-[#F0F4FF]" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <NavigationBar />

        <div className="flex-1 p-4">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl font-bold text-[#1F1F1F] text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Тарифные планы
            </h1>

            {status && (
              <div className="text-center mb-8">
                <p className="text-[#7A7A7A]">
                  На сегодня осталось общений:{' '}
                  <span className="font-bold text-[#1A73E8]" data-testid="remaining-communications">
                    {status.total_available}
                  </span>
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {plans.map((plan, index) => {
                const isEnabled = plan.enabled !== false;
                const color = planColors[index];

                return (
                  <div
                    key={plan.name}
                    className={`rounded-3xl p-8 border-2 transition-all ${
                      !isEnabled
                        ? 'opacity-60 bg-gray-100 border-gray-300'
                        : 'bg-white border-[#E5E5E5] hover:border-[#1A73E8] hover:shadow-xl'
                    }`}
                    data-testid={`plan-card-${plan.name}`}
                  >
                    <div className="text-center mb-6">
                      <div
                        className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg ${
                          !isEnabled ? 'opacity-50' : ''
                        }`}
                        style={{ backgroundColor: color }}
                      >
                        <Check className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2 text-[#1F1F1F]">
                        {plan.name}
                      </h3>
                      <div className="text-4xl font-bold mb-1 text-[#1F1F1F]">
                        {plan.price} ₽<span className="text-base font-normal text-[#7A7A7A]"> в мес.</span>
                      </div>
                      <p className="text-[#7A7A7A] text-sm">
                        +{plan.communications} общений <span className="font-medium">в день</span>
                      </p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center gap-2 text-[#1F1F1F]">
                        <Check className="w-5 h-5 text-[#34C759]" />
                        <span>Дополнительные знакомства</span>
                      </li>
                      <li className="flex items-center gap-2 text-[#1F1F1F]">
                        <Check className="w-5 h-5 text-[#34C759]" />
                        <span>Найти свою пару быстрее</span>
                      </li>
                      <li className="flex items-center gap-2 text-[#1F1F1F]">
                        <Check className="w-5 h-5 text-[#34C759]" />
                        <span>Еще больше фильтров</span>
                      </li>
                    </ul>

                    {!isEnabled && (
                      <p className="text-center text-red-500 text-sm mb-4 font-medium">
                        Тариф временно не доступен
                      </p>
                    )}

                    <Button
                      onClick={() => handlePurchaseClick(plan.name, isEnabled)}
                      disabled={!isEnabled}
                      className={`w-full py-6 rounded-full text-white font-semibold text-lg ${
                        !isEnabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                      }`}
                      style={{ backgroundColor: isEnabled ? color : '#9CA3AF' }}
                      data-testid={`purchase-${plan.name}-button`}
                    >
                      {currentPlan === plan.name ? 'ТЕКУЩИЙ ПЛАН' : isEnabled ? 'ОПЛАТИТЬ' : 'НЕ ДОСТУПЕН'}
                    </Button>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-[#7A7A7A] text-sm mt-8">
              * Оплата переводом по реквизитам. Подписка активируется после проверки администратором.
            </p>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Plan Change */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-center flex items-center justify-center gap-2" style={{ color: '#1F1F1F' }}>
              <AlertTriangle className="w-6 h-6 text-[#FFA726]" />
              Смена тарифа
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-xl p-4 mb-4">
              <p className="text-center text-[#1F1F1F]">
                План будет переключен на новый, прошлый план полностью обнулится. Деньги не возвращаются.
              </p>
            </div>
            <p className="text-center text-[#7A7A7A] text-sm">
              Текущий план: <span className="font-semibold text-[#1F1F1F]">{currentPlan}</span>
              <br />
              Новый план: <span className="font-semibold text-[#1A73E8]">{selectedPlan}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setShowConfirmModal(false);
                setSelectedPlan(null);
              }}
              variant="outline"
              className="flex-1"
            >
              Отмена
            </Button>
            <Button
              onClick={() => openPayment(selectedPlan)}
              className="flex-1"
              style={{ background: 'linear-gradient(135deg, #FF5757 0%, #FF8E8E 100%)' }}
            >
              Подтвердить
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment instructions and confirmation */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => {
        if (!open) {
          closePayment();
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-center" style={{ color: '#1F1F1F' }}>
              Оплата тарифа {selectedPlan}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div
              className="flex items-center justify-center gap-2 rounded-xl border border-[#FFD5D5] bg-[#FFF4F4] px-4 py-3"
              data-testid="payment-countdown"
            >
              <Clock className="h-5 w-5 text-[#E53935]" />
              <span className="text-sm text-[#7A1F1F]">Осталось на оплату:</span>
              <span className="font-mono text-xl font-bold text-[#E53935]">
                {String(Math.floor(paymentSecondsLeft / 60)).padStart(2, '0')}:
                {String(paymentSecondsLeft % 60).padStart(2, '0')}
              </span>
            </div>
            <div className="rounded-xl border border-[#E5E5E5] bg-[#F6F7F9] p-4 space-y-2 text-sm">
              <p><span className="text-[#7A7A7A]">Получатель:</span> <span className="font-semibold">{paymentDetails?.recipient_name}</span></p>
              <div className="flex items-center justify-between gap-3">
                <p><span className="text-[#7A7A7A]">Телефон:</span> <span className="font-semibold select-all">{paymentDetails?.recipient_phone}</span></p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyPaymentPhone}
                  className="shrink-0 gap-1.5"
                  data-testid="copy-payment-phone"
                >
                  <Copy className="h-4 w-4" />
                  Скопировать
                </Button>
              </div>
              <p><span className="text-[#7A7A7A]">Сумма:</span> <span className="font-semibold">{selectedPlanDetails?.price} ₽</span></p>
              <p><span className="text-[#7A7A7A]">Банк:</span> <span className="font-semibold">{paymentDetails?.recipient_bank}</span></p>
              <p><span className="text-[#7A7A7A]">Способ:</span> <span className="font-semibold">{paymentDetails?.payment_method}</span></p>
            </div>
            <div className="rounded-xl border border-[#FFB3B3] bg-[#FFF1F1] p-3 text-sm">
              <p className="font-bold text-[#C62828]">После перевода нажмите «ОПЛАТИЛ(-А)».</p>
              <p className="mt-1 text-[#5F6368]">Заявка уйдёт на проверку, а подписка активируется после подтверждения оплаты.</p>
            </div>
            <div>
              <Label htmlFor="payer-full-name">Имя отправителя перевода</Label>
              <Input
                id="payer-full-name"
                value={payerFullName}
                onChange={(event) => setPayerFullName(event.target.value)}
                placeholder="Как указано в банковском приложении"
                className="mt-2"
                data-testid="payer-full-name"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={closePayment}
              variant="outline"
              className="flex-1"
              disabled={submittingPayment}
            >
              Отмена
            </Button>
            <Button
              onClick={handlePaymentRequest}
              className="flex-1"
              disabled={submittingPayment}
              style={{ background: 'linear-gradient(135deg, #34C759 0%, #5DD97C 100%)' }}
              data-testid="submit-payment-request"
            >
              {submittingPayment ? 'ОТПРАВКА...' : 'ОПЛАТИЛ(-А)'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;
